
import { useState } from "react";
import { Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

interface ShareStartupDialogProps {
  startup: {
    id: string;
    name: string;
    short_description: string;
    category: string;
    stage: string;
  };
}

export function ShareStartupDialog({ startup }: ShareStartupDialogProps) {
  const [content, setContent] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const renderContent = (text: string) => {
    const mentionRegex = /@(\w+)/g;
    const hashtagRegex = /#(\w+)/g;
    let lastIndex = 0;
    const parts = [];
    let match;

    // Handle mentions
    while ((match = mentionRegex.exec(text)) !== null) {
      if (lastIndex < match.index) {
        parts.push(text.slice(lastIndex, match.index));
      }
      parts.push(
        <Link
          key={`mention-${match.index}`}
          to={`/profile/${match[1]}`}
          className="text-[#9b87f5] hover:underline pointer-events-auto z-10 relative inline-block"
        >
          {match[0]}
        </Link>
      );
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      let remainingText = text.slice(lastIndex);
      
      // Handle hashtags in remaining text
      const hashtagParts = [];
      lastIndex = 0;
      
      while ((match = hashtagRegex.exec(remainingText)) !== null) {
        if (lastIndex < match.index) {
          hashtagParts.push(remainingText.slice(lastIndex, match.index));
        }
        hashtagParts.push(
          <Link
            key={`hashtag-${match.index}`}
            to={`/search?q=${match[1]}`}
            className="text-[#9b87f5] hover:underline pointer-events-auto z-10 relative inline-block"
          >
            {match[0]}
          </Link>
        );
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < remainingText.length) {
        hashtagParts.push(remainingText.slice(lastIndex));
      }
      
      parts.push(...hashtagParts);
    }

    return (
      <div className="whitespace-pre-wrap">
        {parts}
      </div>
    );
  };

  const handleShare = async () => {
    if (!user) return;

    try {
      const shouldInvokeDeepVoid = content.includes('@deepvoid');
      const postContent = `${content}\n\nShared startup: ${startup.name}`;

      console.log('Creating post with content:', postContent);
      console.log('Should invoke DeepVoid:', shouldInvokeDeepVoid);

      // Create the initial post
      const { data: post, error } = await supabase
        .from("posts")
        .insert([
          {
            user_id: user.id,
            content: postContent,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      console.log('Post created:', post);

      // If @deepvoid is mentioned, trigger the DeepVoid response
      if (shouldInvokeDeepVoid) {
        try {
          console.log('Invoking DeepVoid function with:', {
            postContent,
            postId: post.id,
            startupContext: {
              name: startup.name,
              description: startup.short_description,
              category: startup.category,
              stage: startup.stage
            }
          });

          const { data, error: functionError } = await supabase.functions.invoke('deepvoid-response', {
            body: { 
              postContent: postContent,
              postId: post.id,
              startupContext: {
                name: startup.name,
                description: startup.short_description,
                category: startup.category,
                stage: startup.stage
              }
            }
          });
          
          if (functionError) {
            console.error('Error getting DeepVoid response:', functionError);
            throw functionError;
          }

          console.log('DeepVoid responded successfully:', data);
        } catch (error) {
          console.error('Error invoking DeepVoid function:', error);
          toast({
            title: "Note",
            description: "Post shared, but there was an issue with DeepVoid. Please try mentioning @deepvoid again in a new post.",
            variant: "destructive"
          });
        }
      }

      toast({
        title: "Success",
        description: shouldInvokeDeepVoid 
          ? "Startup shared! DeepVoid is thinking about your post..."
          : "Startup shared to your feed!",
      });
      
      setIsOpen(false);
      setContent("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to share startup",
        variant: "destructive",
      });
    }
  };

  const handleExternalShare = async () => {
    try {
      const shareData = {
        title: startup.name,
        text: `Check out ${startup.name} on FluxVoid!\n${startup.short_description}`,
        url: `${window.location.origin}/communities/startups/${startup.id}`,
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast({
          title: "Link copied!",
          description: "Share this link with others",
        });
      }
    } catch (error: any) {
      console.error("Error sharing:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share {startup.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="relative">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={`Share ${startup.name}\n\nTip: Mention @deepvoid to get startup advice and insights!`}
              className="resize-none min-h-[100px] text-transparent bg-clip-text"
            />
            <div className="absolute inset-0 pointer-events-none">
              <div className="p-3 text-white">
                {renderContent(content)}
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleShare} className="flex-1">
              Share to Feed
            </Button>
            <Button onClick={handleExternalShare} variant="outline" className="flex-1">
              Share Externally
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
